// competition.js — F.4 orchestrator: 5-tab Competition Intelligence view.
// Tab pattern mirrors client-card.js (TABS/TAB_LABELS/renderTabBar/wireTabClicks).

import { apiFetch, runManualAction } from './utils.js';
import { state } from './state.js';
import { renderScheduleTab, initScheduleHandlers } from './competition-schedule.js';
import { renderBandsTab, loadBandsList, renderBandDetail, initBandsHandlers } from './competition-bands.js';
import { renderResultsTab, loadResultsSelectors, initResultsHandlers } from './competition-results.js';
import { renderAnalyticsTab, initAnalyticsTab } from './competition-analytics.js';
import { renderFormatsTab, loadFormatsTab, initFormatsHandlers } from './competition-formats.js';

const TABS = ['schedule', 'bands', 'results', 'analytics', 'formats'];
const TAB_LABELS = {
  schedule:  'Schedule',
  bands:     'Bands',
  results:   'Results',
  analytics: 'Analytics',
  formats:   'Formats',
};

// Initialize all window handlers once at module load
initScheduleHandlers();
initBandsHandlers();
initResultsHandlers();
initFormatsHandlers();

export function initCompetition() {
  window.renderCompetitionView = renderCompetitionView;
  window.loadCompTab           = loadTabContent;
}

export async function renderCompetitionView() {
  state.currentView = 'competition';
  const container = document.getElementById('view-competition');
  if (!container) return;

  container.innerHTML = '<div class="loading-state">Loading…</div>';

  const activeTab = state.currentCompTab || 'schedule';

  // Pre-fetch client intelligence + clients list (used by schedule tab Add Event modal)
  const [intel, clients] = await Promise.all([
    apiFetch('/api/competition-analysis/client-intelligence').catch(() => []),
    apiFetch('/api/competition/clients').catch(() => []),
  ]);

  container.innerHTML = `
    ${renderIntelHeader(intel)}
    <div class="comp-tab-bar" style="display:flex;gap:.25rem;border-bottom:1px solid rgba(255,255,255,.07);margin-bottom:1rem;padding-bottom:0">
      ${TABS.map(tab => `
        <button class="comp-tab-btn card-tab ${tab === activeTab ? 'active' : ''}"
          data-tab="${tab}" onclick="window._compTabClick('${tab}')">
          ${TAB_LABELS[tab]}
        </button>
      `).join('')}
    </div>
    <div id="comp-tab-content"></div>
  `;

  window._compClients = clients;

  window.runWeeklyAnalysis = async function() {
    const btn = document.getElementById('weekly-analysis-btn');
    if (btn) { btn.textContent = 'Running…'; btn.disabled = true; }
    try {
      const result = await apiFetch('/api/competition-analysis/weekly-analysis', { method: 'POST' });
      alert(`Weekly analysis complete: ${result.processed} band${result.processed !== 1 ? 's' : ''} processed. Check action queue for email drafts.`);
    } catch (err) {
      alert('Weekly analysis failed: ' + err.message);
    } finally {
      if (btn) { btn.textContent = 'Generate Weekly Analysis'; btn.disabled = false; }
    }
  };

  window.compRunScrape = async function() {
    const btn = document.getElementById('comp-scrape-btn');
    if (btn) { btn.textContent = '⏳ Running…'; btn.disabled = true; }
    try {
      await runManualAction('run_competition_scrape', {}, 'ma-status-run_competition_scrape-g');
    } finally {
      if (btn) { btn.textContent = '▶ Run Scrape Now'; btn.disabled = false; }
    }
  };

  window._compTabClick = function(tab) {
    document.querySelectorAll('.comp-tab-btn').forEach(b => b.classList.remove('active'));
    document.querySelector(`.comp-tab-btn[data-tab="${tab}"]`)?.classList.add('active');
    state.currentCompTab = tab;
    loadTabContent(tab);
  };

  await loadTabContent(activeTab);
}

function renderIntelHeader(intel) {
  if (!intel || !intel.length) return '';

  const rows = intel.map(b => {
    const deltaNum = b.delta;
    const deltaEl = deltaNum != null
      ? `<span style="color:${deltaNum >= 0 ? '#4caf50' : '#ef5350'};font-size:.75rem">${deltaNum >= 0 ? '+' : ''}${parseFloat(deltaNum).toFixed(2)}</span>`
      : '';
    const trendIcon  = deltaNum > 0 ? '▲' : deltaNum < 0 ? '▼' : '—';
    const trendColor = deltaNum > 0 ? '#4caf50' : deltaNum < 0 ? '#ef5350' : '#888';
    return `
      <div class="intel-band-row" onclick="window._compTabClick('bands');window.openBandDetail(${b.band_id})"
        style="display:flex;gap:1rem;align-items:center;padding:.5rem .75rem;cursor:pointer;border-radius:6px;transition:background .15s"
        onmouseover="this.style.background='rgba(255,255,255,.04)'" onmouseout="this.style.background=''">
        <div style="flex:1;min-width:0">
          <div style="font-size:.85rem;font-weight:600;white-space:nowrap;overflow:hidden;text-overflow:ellipsis">${b.band_name}</div>
          <div style="font-size:.75rem;color:var(--text-muted)">${b.org_name || ''}</div>
        </div>
        <div style="text-align:right;white-space:nowrap">
          <div style="font-size:.9rem;font-variant-numeric:tabular-nums">${b.latest_score != null ? parseFloat(b.latest_score).toFixed(3) : '—'}</div>
          <div>${deltaEl}</div>
        </div>
        <div style="text-align:center;white-space:nowrap;min-width:50px">
          <div style="font-size:.75rem;color:var(--text-muted)">Show ${b.show_count || '?'}</div>
          <div style="font-size:.75rem;color:var(--text-muted)">${b.placement ? `#${b.placement}` : ''}</div>
        </div>
        <div style="color:${trendColor};font-size:.8rem;min-width:16px">${trendIcon}</div>
      </div>`;
  }).join('');

  return `
    <div style="margin-bottom:1rem;padding:.75rem;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:8px">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:.5rem">
        <div style="font-size:.78rem;font-weight:600;color:var(--text-muted);text-transform:uppercase;letter-spacing:.05em">
          WMM Client Intelligence — ${new Date().getFullYear()} Season
        </div>
        <div style="display:flex;gap:6px;align-items:center;">
          <button class="btn-add-inline" onclick="window.runWeeklyAnalysis()" id="weekly-analysis-btn"
            style="font-size:.72rem;padding:.25rem .6rem">
            Generate Weekly Analysis
          </button>
          <button class="btn-add-inline" onclick="window.compRunScrape()" id="comp-scrape-btn"
            style="font-size:.72rem;padding:.25rem .6rem;background:rgba(255,170,0,.12);border-color:rgba(255,170,0,.3);color:#ffaa00">
            ▶ Run Scrape Now
          </button>
          <span class="auto-inline-hint" id="ma-status-run_competition_scrape-g" style="font-size:.72rem;"></span>
        </div>
      </div>
      <div style="display:grid;grid-template-columns:repeat(auto-fit,minmax(220px,1fr));gap:.25rem">
        ${rows}
      </div>
    </div>`;
}

async function loadTabContent(tab) {
  const content = document.getElementById('comp-tab-content');
  if (!content) return;
  content.innerHTML = '<div class="loading-state">Loading…</div>';

  try {
    switch (tab) {
      case 'schedule': {
        const schedule = await apiFetch('/api/competition/schedule').catch(() => []);
        const clients  = window._compClients || [];
        content.innerHTML = await renderScheduleTab(schedule, clients);
        break;
      }

      case 'bands':
        content.innerHTML = await renderBandsTab();
        await loadBandsList();
        break;

      case 'results':
        content.innerHTML = await renderResultsTab();
        await loadResultsSelectors();
        break;

      case 'analytics':
        content.innerHTML = await renderAnalyticsTab();
        await initAnalyticsTab();
        break;

      case 'formats':
        content.innerHTML = await renderFormatsTab();
        await loadFormatsTab();
        break;
    }
  } catch (err) {
    content.innerHTML = `<div class="error-state">Failed to load ${TAB_LABELS[tab] || tab}: ${err.message}</div>`;
  }
}
